/**
 * MKS-464 — People view of the Delivery tab.
 *
 * "Contributed" = delivered tasks (this period) the person booked hours on, up to the task's
 * delivery day, without being the assignee credited at delivery (MKS-464 round 2).
 *
 * "Specified" = delivered tasks (this period) the person created, wrote acceptance criteria for,
 * answered a Factory request on or edited the description of, up to the delivery moment, without
 * being the credited assignee (MKS-464 round 3). It outranks Contributed: a task the person both
 * specified and booked hours on is counted under Specified only (precedence Delivered > Specified >
 * Contributed, settled server-side).
 *
 * "Total" = Delivered + Specified + Contributed. The tiers are exclusive per task, so Total is the
 * number of distinct delivered tasks the person was involved in.
 *
 * Rows are split in two sections: people who delivered, specified or contributed to something in
 * the period ("Delivered"), and people with none of the three (on-leave people included, marked by
 * their badge). Columns read Person, Total, Delivered, Specified, Contributed, In progress, Hours,
 * Coverage, Last delivery. Every column header sorts both sections the same way; the default is
 * total desc, then delivered desc, then specified desc, then contributed desc, then hours desc,
 * then name — so "hours but no tasks" rises to the top of the No delivery section. A person row
 * expands to the tasks they delivered (by day), the tasks they specified (by delivery day, with
 * role tags), the tasks they contributed to (by delivery day, with their hours) and the tasks they
 * have in progress. After the people comes the "Factory/Robot & unassigned" row: deliveries and
 * in-progress work not credited to an active internal person; each of its deliveries names its
 * human co-authors ("specified by ...").
 *
 * MKS-520 — a "Trend" column sits right after Total: 8 small bars per person, the Total for each
 * of the last 8 Europe/Prague calendar weeks, oldest first, the current partial week last and
 * lighter. Bars are scaled to the largest weekly total across all people. The column is not
 * sortable.
 */
import { Fragment, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../ui/Avatar';
import { DeliveryTaskList } from './DeliveryTaskList';
import type {
  CoverageTone,
  DeliveryBotsGroup,
  DeliveryPersonRow,
  DeliveryTrendWeek,
  DeliveryWeeklyTrend,
} from '../../services/api/delivery';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  formatHours,
  formatName,
} from '../../utils/format';
import { formatDayMonth } from '../../utils/dateFormat';
import { compareText } from '../../utils/localeFormat';

// Literal class strings: Tailwind's JIT scans source text, so they must not be built by interpolation.
// MKS-520 — the Trend column sits after Total.
const GRID_WITH_TREND = 'grid grid-cols-[1fr_80px_100px_90px_90px_90px_90px_80px_90px_150px]';

const TREND_COLUMN_TITLE = 'Total per week for the last 8 weeks, current week last.';

// Hover explanation per column header.
const COLUMN_TITLES: Record<PeopleSortKey, string> = {
  name: 'Active internal people. Click a row to see its tasks.',
  total:
    'Delivered tasks this person was involved in: Delivered + Specified + Contributed. ' +
    'Each task counts once, in its highest tier.',
  delivered:
    'Tasks moved to a done column in this period with this person as the assignee at delivery.',
  specified:
    'Delivered tasks this person created, wrote acceptance criteria for, answered a Factory ' +
    'question on or edited the description of, without being the assignee.',
  contributed:
    'Delivered tasks this person logged hours on (up to the delivery day) without being the ' +
    'assignee or a specifier.',
  inProgress: 'Tasks assigned to this person that sit in an in-progress column right now.',
  hours: 'All hours this person logged in this period, on any project.',
  coverage:
    'Share of the logged hours that are booked on a task. Red: no hours on tasks; ' +
    'amber: under 50%.',
  lastDelivery: 'When this person last delivered a task (looks back up to 90 days).',
};

const SECTIONS: Array<{ key: 'delivered' | 'none'; label: string; tone: string }> = [
  { key: 'delivered', label: 'Delivered', tone: 'text-ms-text-secondary' },
  { key: 'none', label: 'No delivery', tone: 'text-rose-600' },
];

export type PeopleSortKey =
  | 'name'
  | 'total'
  | 'delivered'
  | 'specified'
  | 'contributed'
  | 'inProgress'
  | 'hours'
  | 'coverage'
  | 'lastDelivery';
type SortDir = 'asc' | 'desc';

type Column = { key: PeopleSortKey; label: string; align: 'left' | 'right' };

const PERSON_COLUMN: Column = { key: 'name', label: 'Person', align: 'left' };

const NUMBER_COLUMNS: Column[] = [
  { key: 'delivered', label: 'Delivered', align: 'right' },
  { key: 'specified', label: 'Specified', align: 'right' },
  { key: 'contributed', label: 'Contributed', align: 'right' },
  { key: 'inProgress', label: 'In progress', align: 'right' },
  { key: 'hours', label: 'Hours', align: 'right' },
  { key: 'coverage', label: 'Coverage', align: 'right' },
  { key: 'lastDelivery', label: 'Last delivery', align: 'left' },
];

const COLUMNS_WITH_TOTAL: Column[] = [
  PERSON_COLUMN,
  { key: 'total', label: 'Total', align: 'right' },
  ...NUMBER_COLUMNS,
];

function totalOf(row: DeliveryPersonRow): number {
  return row.delivered + row.specified + row.contributed;
}

function sortValue(row: DeliveryPersonRow, key: PeopleSortKey): string | number | null {
  switch (key) {
    case 'name':
      return formatName(row.person);
    case 'total':
      return totalOf(row);
    case 'delivered':
      return row.delivered;
    case 'specified':
      return row.specified;
    case 'contributed':
      return row.contributed;
    case 'inProgress':
      return row.inProgress;
    case 'hours':
      return row.hours;
    case 'coverage':
      return row.coveragePct;
    case 'lastDelivery':
      return row.lastDeliveryAt ? Date.parse(row.lastDeliveryAt) : null;
  }
}

function compareBy(key: PeopleSortKey, dir: SortDir) {
  return (a: DeliveryPersonRow, b: DeliveryPersonRow): number => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    // Empty values (no coverage, never delivered) stay at the bottom in both directions.
    if (va === null || vb === null) {
      if (va !== vb) return va === null ? 1 : -1;
    } else if (va !== vb) {
      const cmp =
        typeof va === 'string' && typeof vb === 'string'
          ? compareText(va, vb)
          : (va as number) - (vb as number);
      if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    }
    // Tie-break (never flips with the direction): when sorting by the total, more deliveries first;
    // when sorting by the total or by deliveries, more specified, then more contributions first;
    // then more hours; then by name.
    if (key === 'total' && a.delivered !== b.delivered) return b.delivered - a.delivered;
    if (key === 'total' || key === 'delivered') {
      if (a.specified !== b.specified) return b.specified - a.specified;
      if (a.contributed !== b.contributed) return b.contributed - a.contributed;
    }
    if (key !== 'hours' && a.hours !== b.hours) return b.hours - a.hours;
    return compareText(formatName(a.person), formatName(b.person));
  };
}

const COVERAGE_TONE: Record<CoverageTone, string> = {
  red: 'text-rose-600',
  amber: 'text-amber-600',
  ok: 'text-ms-text-secondary',
};

const DASH = '—';

/** Bar height in px: a 2 px stub for zero, else at least 3 px and 20 px for the largest total. */
function trendBarHeightPx(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 2;
  return Math.max(3, Math.round((value / max) * 20));
}

/** `26.10. – 1.11.2026`, with the start's year spelled out only when the two years differ. */
function formatTrendRange(week: DeliveryTrendWeek): string {
  const start =
    week.weekStart.slice(0, 4) === week.weekEnd.slice(0, 4)
      ? formatDayMonth(week.weekStart)
      : formatDisplayDate(week.weekStart);
  return `${start} – ${formatDisplayDate(week.weekEnd)}`;
}

/**
 * One person's 8 weekly bars, or a muted dash while the trend is loading / failed / lacks the
 * person. Spans only: the cell sits inside the row's <button>, where a <div> is not allowed.
 */
function TrendCell({
  personId,
  weeks,
  totals,
  max,
}: {
  personId: number;
  weeks: DeliveryTrendWeek[] | undefined;
  totals: number[] | undefined;
  max: number;
}) {
  if (!weeks || weeks.length === 0 || !totals) {
    return (
      <span data-testid={`trend-${personId}`} className="px-2 py-2.5 text-right text-ms-text-muted">
        {DASH}
      </span>
    );
  }
  const last = weeks.length - 1;
  return (
    <span
      data-testid={`trend-${personId}`}
      className="px-2 py-2.5 flex items-end justify-end gap-0.5 h-full"
    >
      <span
        className="flex items-end gap-0.5 h-5"
        role="img"
        aria-label={`Total per week, oldest first: ${totals.join(', ')}`}
      >
        {weeks.map((week, i) => {
          const value = totals[i] ?? 0;
          // `ms-*` colors are plain CSS variables, so an opacity modifier on them does nothing:
          // the current week is lightened with the separate opacity utility.
          const tone = value > 0 ? 'bg-ms-orange' : 'bg-ms-border';
          return (
            <span
              key={week.weekStart}
              data-testid={`trend-bar-${personId}-${i}`}
              title={`${formatTrendRange(week)}: ${value}`}
              style={{ height: `${trendBarHeightPx(value, max)}px` }}
              className={`block w-1.5 rounded-sm ${tone}${i === last ? ' opacity-40' : ''}`}
            />
          );
        })}
      </span>
    </span>
  );
}

export interface DeliveryPeopleTableProps {
  people: DeliveryPersonRow[];
  bots: DeliveryBotsGroup;
  /** MKS-520 — the weekly trend; undefined while loading or after an error (cells show a dash). */
  trend?: DeliveryWeeklyTrend;
}

export function DeliveryPeopleTable({ people, bots, trend }: DeliveryPeopleTableProps) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: PeopleSortKey; dir: SortDir }>({
    key: 'total',
    dir: 'desc',
  });

  const sections = useMemo(() => {
    const sorted = [...people].sort(compareBy(sort.key, sort.dir));
    return {
      delivered: sorted.filter((p) => p.delivered > 0 || p.specified > 0 || p.contributed > 0),
      none: sorted.filter((p) => p.delivered === 0 && p.specified === 0 && p.contributed === 0),
    };
  }, [people, sort.key, sort.dir]);

  function sortBy(key: PeopleSortKey) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : // Names read A→Z first; numbers and dates biggest/newest first.
          { key, dir: key === 'name' ? 'asc' : 'desc' },
    );
  }

  function toggle(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const showBots = bots.delivered + bots.inProgress > 0;
  const grid = GRID_WITH_TREND;

  const { trendByPerson, trendMax } = useMemo(() => {
    const byPerson = new Map<number, number[]>();
    let max = 0;
    for (const p of trend?.people ?? []) {
      byPerson.set(p.personId, p.totals);
      for (const v of p.totals) if (v > max) max = v;
    }
    return { trendByPerson: byPerson, trendMax: max };
  }, [trend]);

  return (
    <div className="bg-ms-surface border border-ms-border-subtle rounded-lg overflow-hidden text-[12.5px]">
      <div
        className={`${grid} sticky top-0 bg-ms-surface-2 border-b border-ms-border-subtle text-[10.5px] uppercase tracking-wider font-medium text-ms-text-muted`}
      >
        {COLUMNS_WITH_TOTAL.map((col, i) => {
          const active = sort.key === col.key;
          const pad = i === 0 || i === COLUMNS_WITH_TOTAL.length - 1 ? 'px-4' : 'px-2';
          return (
            <Fragment key={col.key}>
              <div
                role="columnheader"
                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                title={COLUMN_TITLES[col.key]}
                className={`${pad} py-2 ${col.align === 'right' ? 'text-right' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => sortBy(col.key)}
                  className={`inline-flex items-center gap-0.5 uppercase tracking-wider hover:text-ms-text-strong focus:outline-none focus:ring-2 focus:ring-ms-orange/40 rounded ${
                    active ? 'text-ms-text-strong' : ''
                  }`}
                >
                  {col.label}
                  {active &&
                    (sort.dir === 'asc' ? (
                      <ChevronUpIcon className="h-3 w-3" aria-hidden="true" />
                    ) : (
                      <ChevronDownIcon className="h-3 w-3" aria-hidden="true" />
                    ))}
                </button>
              </div>
              {col.key === 'total' && (
                <div
                  role="columnheader"
                  title={TREND_COLUMN_TITLE}
                  data-testid="trend-header"
                  className="px-2 py-2 text-right"
                >
                  Trend
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      {SECTIONS.map((section) => {
        const rows = sections[section.key];
        if (rows.length === 0) return null;
        return (
          <section key={section.key}>
            <div
              data-testid={`delivery-section-${section.key}`}
              className={`flex items-center gap-2 px-4 py-1.5 bg-ms-surface-2/50 border-b border-ms-border-subtle text-[11px] font-semibold uppercase tracking-wider ${section.tone}`}
            >
              <span>{section.label}</span>
              <span className="font-medium">{rows.length}</span>
            </div>
            {rows.map((row) => {
              const key = `person-${row.person.id}`;
              const expanded = open.has(key);
              const panelId = `delivery-panel-${key}`;
              const name = formatName(row.person);
              return (
                <div key={key} className="border-b border-ms-border-subtle">
                  <button
                    type="button"
                    className={`${grid} w-full items-center text-left hover:bg-ms-surface-2/40 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ms-orange/40`}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => toggle(key)}
                  >
                    <span className="px-4 py-2.5 flex items-center gap-2 min-w-0">
                      <Avatar
                        name={name}
                        colorSeq={row.person.avatarColorSeq}
                        avatarUrl={row.person.avatarUrl}
                        size="xs"
                      />
                      <span className="font-medium text-ms-text-strong truncate">{name}</span>
                      {row.onLeave && (
                        <span
                          data-testid="on-leave-badge"
                          className="shrink-0 rounded-full bg-ms-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ms-text-secondary border border-ms-border-subtle"
                        >
                          On leave
                        </span>
                      )}
                    </span>
                    <span
                      data-testid={`total-${row.person.id}`}
                      className="px-2 py-2.5 text-right tabular-nums font-medium text-ms-text-strong"
                    >
                      {totalOf(row)}
                    </span>
                    <TrendCell
                      personId={row.person.id}
                      weeks={trend?.weeks}
                      totals={trendByPerson.get(row.person.id)}
                      max={trendMax}
                    />
                    <span className="px-2 py-2.5 text-right tabular-nums">{row.delivered}</span>
                    <span
                      data-testid={`specified-${row.person.id}`}
                      className="px-2 py-2.5 text-right tabular-nums"
                    >
                      {row.specified}
                    </span>
                    <span
                      data-testid={`contributed-${row.person.id}`}
                      className="px-2 py-2.5 text-right tabular-nums"
                    >
                      {row.contributed}
                    </span>
                    <span className="px-2 py-2.5 text-right tabular-nums">{row.inProgress}</span>
                    <span
                      data-testid={`hours-${row.person.id}`}
                      className={`px-2 py-2.5 text-right tabular-nums ${
                        // Hours without a delivery, specification or contribution: the case
                        // managers want to spot.
                        row.delivered === 0 &&
                        row.specified === 0 &&
                        row.contributed === 0 &&
                        row.hours > 0
                          ? 'text-amber-600 font-medium'
                          : ''
                      }`}
                    >
                      {formatHours(row.hours)}
                    </span>
                    <span
                      data-testid={`coverage-${row.person.id}`}
                      className={`px-2 py-2.5 text-right tabular-nums ${
                        row.coverageTone ? COVERAGE_TONE[row.coverageTone] : 'text-ms-text-muted'
                      }`}
                    >
                      {row.coveragePct === null ? DASH : `${row.coveragePct}%`}
                    </span>
                    <span className="px-4 py-2.5 text-ms-text-secondary">
                      {row.lastDeliveryAt ? formatDisplayDateTime(row.lastDeliveryAt) : DASH}
                    </span>
                  </button>
                  {expanded && (
                    <div id={panelId} className="px-4 pb-3 pt-1 bg-ms-surface-2/20">
                      <DeliveryTaskList
                        deliveredTasks={row.deliveredTasks}
                        specifiedTasks={row.specifiedTasks}
                        contributedTasks={row.contributedTasks}
                        inProgressTasks={row.inProgressTasks}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}

      {showBots && (
        <div className="border-t border-ms-border-subtle">
          <button
            type="button"
            className={`${grid} w-full items-center text-left hover:bg-ms-surface-2/40 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ms-orange/40`}
            aria-expanded={open.has('bots')}
            aria-controls="delivery-panel-bots"
            onClick={() => toggle('bots')}
          >
            <span className="px-4 py-2.5 font-medium text-ms-text-strong">
              Factory/Robot &amp; unassigned
            </span>
            {/* The group only delivers: its total is its delivered count. */}
            <span
              data-testid="total-bots"
              className="px-2 py-2.5 text-right tabular-nums font-medium text-ms-text-strong"
            >
              {bots.delivered}
            </span>
            {/* MKS-520: the group has no weekly series; the dash keeps the grid aligned. */}
            <span data-testid="trend-bots" className="px-2 py-2.5 text-right text-ms-text-muted">
              {DASH}
            </span>
            <span className="px-2 py-2.5 text-right tabular-nums">{bots.delivered}</span>
            {/* SD-15: the group has no specified count; its co-authors show per task. */}
            <span className="px-2 py-2.5 text-right text-ms-text-muted">{DASH}</span>
            <span className="px-2 py-2.5 text-right text-ms-text-muted">{DASH}</span>
            <span className="px-2 py-2.5 text-right tabular-nums">{bots.inProgress}</span>
            <span className="px-2 py-2.5 text-right text-ms-text-muted">{DASH}</span>
            <span className="px-2 py-2.5 text-right text-ms-text-muted">{DASH}</span>
            <span className="px-4 py-2.5 text-ms-text-muted">{DASH}</span>
          </button>
          {open.has('bots') && (
            <div id="delivery-panel-bots" className="px-4 pb-3 pt-1 bg-ms-surface-2/20">
              <DeliveryTaskList
                deliveredTasks={bots.deliveredTasks}
                inProgressTasks={bots.inProgressTasks}
                showAssignee
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
